import { BannerProperty, BannerPropertyKey } from '../../_shared';
import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LoaderService,
  AudienceService,
  AlertService,
  FunnelService,
  FunnelStepService,
  PusherService,
  CampaignService,
  UserService,
} from '../../_services';
import {
  Audience,
  AudienceDateTypes,
  AudiencePopup,
  AudienceStatus,
  AudienceStatusLabels,
  getAudienceConditions,
  Breadcrumb,
  OfferStages,
  NotificationType,
  OfferTypes,
  Funnel,
  CrudPopupTitleMap,
  User,
} from '../../_models';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { CrudPagedListComponent } from '../../_directives';
import * as moment from 'moment-timezone';
import { StepHeaderDetails } from '../../_models/steps';
import { PopupData, RetentionOffer } from '../retention/retention';
import { SurveyStep } from '../survey/survey';
import { takeUntil } from 'rxjs/operators';
import { getOfferBuilderRoot, OfferBuilderSection } from '../../steps-library/step-util';

@Component({
  moduleId: module.id.toString(),
  selector: 'segments-component',
  templateUrl: './segments.component.html',
  styleUrls: ['./segments.component.scss'],
})
export class SegmentsComponent extends CrudPagedListComponent implements OnInit {
  offer: Funnel;
  user: User;
  breadcrumbs: Breadcrumb[] = [];
  surveyStep: SurveyStep;
  campaignId: number;
  audiencePopup = AudiencePopup;
  audienceStatus = AudienceStatus;
  audienceStatusLabels = AudienceStatusLabels;
  audienceDateTypes = AudienceDateTypes;
  stepHeaderDetails = StepHeaderDetails;
  offerId: number;
  offerData: any;
  popupTitle: string = '';
  tableData: Audience[] = [];
  originalTableData: Audience[] = [...this.tableData];
  selectedFilter: AudienceStatus = AudienceStatus.Active;
  showFilterMenu = false;
  popupData: PopupData = {
    name: '',
    offer_id: null,
    segment_funnel_id: null,
    audience_id: null,
    offer_type: OfferTypes.Downsell,
    offer_intent: null,
    conditions: null,
    popupType: null,
  };
  isPublishable: boolean = false;
  isPublishing: boolean = false;
  currentSection: OfferBuilderSection = OfferBuilderSection.Retention;
  properties: BannerProperty[] = [
    { key: BannerPropertyKey.Id, label: 'ID', value: null },
    { key: BannerPropertyKey.Status, label: 'Status', value: null },
    { key: BannerPropertyKey.Survey, label: 'Survey', value: null },
    { key: BannerPropertyKey.LastModified, label: 'Last Modified', value: null },
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected loader: LoaderService,
    protected audienceService: AudienceService,
    protected alertService: AlertService,
    protected funnelService: FunnelService,
    protected funnelStepService: FunnelStepService,
    public ngxSmartModalService: NgxSmartModalService,
    protected pusherService: PusherService,
    protected campaignService: CampaignService,
    protected userService: UserService
  ) {
    super(router, location, route, audienceService, alertService);
  }

  ngOnInit() {
    this.loader.show();
    this.currentSection = this.route.snapshot.data['section'];
    this.route.params.subscribe((params) => {
      this.offerId = params['offer_id'];
      this.generateBreadcrumbs();
    });

    this.userService
      .getCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        this.user = user;
      });

    this.fetchOffer();

    if (this.pusherService.channel) {
      this.pusherService.channel.bind(NotificationType[NotificationType.COPY_AUDIENCE], (data: any) => {
        data = data.payload;
        if (data && data.is_completed) {
          this.fetchOffer();
        }
      });
    }
  }

  fetchOffer(): void {
    this.funnelService.get(this.offerId).subscribe(
      (offer: Funnel) => {
        this.offer = offer;
        this.isPublishable = offer.is_modified;
        const lastModifiedMoment = moment(offer.updated).tz('EST');

        const lastModified = {
          time: lastModifiedMoment.format('HH:mm:ss z'), // "16:18:10 EST"
          date: lastModifiedMoment.format('DD MMM, YYYY'), // "20 Jan, 2025"
        };

        this.offerData = {
          ...offer,
          id: offer.id,
          displayId: offer.display_id,
          offerName: OfferStages[offer.offer_intent],
          status: offer.is_active ? AudienceStatus.Active : AudienceStatus.Inactive,
          lastModified: `${lastModified.date} - ${lastModified.time}`,
          steps: offer.steps || [],
        };

        this.popupData.offer_intent = offer.offer_intent;

        this.surveyStep = offer.survey ? offer.survey : null;
        this.updateBannerProperties(this.offerData);
        this.setSurveyProperty(this.surveyStep);

        if (this.offerData.segments && this.offerData.segments.length > 0) {
          this.fetchSegments();
        }

        this.loader.hide();
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  setSurveyProperty(survey: SurveyStep): void {
    const surveyProp = this.properties.find((prop) => prop.key === BannerPropertyKey.Survey);

    if (survey) {
      surveyProp.value = survey.name;
      surveyProp.route = [
        'survey',
        'stage',
        survey.funnel,
        'category',
        OfferTypes.Survey,
        survey.category,
        'step',
        survey.id,
      ];
    } else {
      surveyProp.value = '--';
      surveyProp.route = null;
    }
  }

  fetchSegments(): void {
    this.tableData = this.offerData.segments.map((item: any) => {
      const audience = item.audience;
      const lastModifiedMoment = moment(item.updated).tz('EST');

      const lastModified = {
        time: lastModifiedMoment.format('HH:mm:ss z'), // "16:18:10 EST"
        date: lastModifiedMoment.format('DD MMM, YYYY'), // "20 Jan, 2025"
      };

      const conditions = getAudienceConditions(audience);

      return {
        audience: audience.id,
        id: audience.id,
        segment_id: item.id,
        audienceName: audience.name,
        status: this.offerData.status,
        conditions,
        lastModified,
        showAllConditions: conditions.length ? false : true,
        funnel_id: item.id,
        survey: item.survey || null,
      };
    });
    this.originalTableData = this.tableData;
  }

  openSegmentCrudPopup(
    popupType: number,
    audienceId?: number | null,
    audienceName?: string,
    conditions?: string[] | null,
    funnel_id?: number | null
  ): void {
    this.popupTitle = CrudPopupTitleMap[popupType];

    this.popupData = {
      ...this.popupData,
      name: audienceName || '',
      offer_id: this.offerId,
      segment_funnel_id: funnel_id || null,
      audience_id: audienceId || null,
      conditions: conditions || null,
      popupType,
    };

    this.ngxSmartModalService.getModal('segmentCrudPopup').open();
  }

  saveSegment(event): void {
    this.closeSegmentCrudPopup();

    const isNew = this.popupData.popupType === AudiencePopup.Create;

    if (this.popupData.popupType === AudiencePopup.Delete) {
      this.removeSegmentFromList(this.popupData.segment_funnel_id);
    } else if (event && event.audienceId) {
      // Existing audience selected -> create the segment directly, skipping the conditions popup
      this.generateSegmentForExistingAudience(event.audienceId);
    } else if (event && event.id && event.editConditions) {
      this.openSegmentConditionsPopup(event.id, isNew);
    } else if (event && event.name && event.offer_id) {
      this.openSegmentConditionsPopup(event.id, isNew, event.name);
    } else {
      this.fetchOffer();
    }
  }

  removeSegmentFromList(funnelId: number | null): void {
    this.tableData = this.tableData.filter((item: any) => item.funnel_id !== funnelId);
    this.originalTableData = this.tableData;

    if (this.offerData && this.offerData.segments) {
      this.offerData.segments = this.offerData.segments.filter((segment: any) => segment.id !== funnelId);
    }
  }

  generateSegmentForExistingAudience(audienceId: number): void {
    this.loader.show();

    this.audienceService
      .generateSegment(audienceId, { parent_path: this.offerId })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.alertService.success('Segment created successfully.', true);
          this.fetchOffer();
        },
        (error) => {
          this.loader.hide();
          this.handleError(error);
        }
      );
  }

  publish() {
    if (this.offer && this.offer.is_modified) {
      this.isPublishing = true;
      this.funnelService
        .publish(this.offer.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.offer.is_modified = false;
            this.isPublishable = false;
            this.alertService.success('Path changes are being published.', true);
            this.isPublishing = false;
          },
          (error) => {
            this.handleError(error);
            this.isPublishing = false;
          }
        );
    }
  }

  closeSegmentCrudPopup(): void {
    this.ngxSmartModalService.getModal('segmentCrudPopup').close();
  }

  openSegmentConditionsPopup(audienceId: number, isNew?: boolean, audienceName?: string): void {
    this.popupTitle = 'Define Audience Conditions';
    this.popupData = {
      ...this.popupData,
      offer_id: this.offerId,
      audience_id: audienceId,
      name: audienceName,
      popupType: isNew ? AudiencePopup.Create : AudiencePopup.Update,
    };
    this.ngxSmartModalService.getModal('segmentConditionsPopup').open();
  }

  onSegmentBack(): void {
    this.ngxSmartModalService.getModal('segmentConditionsPopup').close();

    this.popupTitle = CrudPopupTitleMap[AudiencePopup.Create];
    this.popupData = {
      ...this.popupData,
      popupType: AudiencePopup.Create,
    };
    this.ngxSmartModalService.getModal('segmentCrudPopup').open();
  }

  saveSegmentConditions(): void {
    this.closeSegmentConditionsPopup();
    this.fetchOffer();
  }

  updateBannerProperties(offer: RetentionOffer): void {
    const valueMap: Partial<Record<BannerPropertyKey, any>> = {
      [BannerPropertyKey.Id]: offer.id ? String(offer.id) : null,
      [BannerPropertyKey.Status]: AudienceStatusLabels[this.offerData.status],
      [BannerPropertyKey.LastModified]: this.offerData.lastModified,
    };

    this.properties = this.properties.map((prop) => {
      if (prop.key in valueMap) {
        return { ...prop, value: valueMap[prop.key] };
      }
      return prop;
    });
  }

  closeSegmentConditionsPopup(): void {
    this.ngxSmartModalService.getModal('segmentConditionsPopup').close();
  }

  closeOtherMenus(currentItem: any): void {
    this.tableData.forEach((item) => {
      if (item.id !== currentItem.id) {
        item.showThreeDotMenu = false;
      }
    });
  }

  toggleThreeDotMenu(item: any): void {
    this.closeOtherMenus(item);
    item.showThreeDotMenu = !item.showThreeDotMenu;
  }

  closeThreeDotMenu(item: any) {
    item.showThreeDotMenu = false;
  }

  toggleShowAllConditions(item: any): void {
    item.showAllConditions = !item.showAllConditions;
  }

  navigate(url: any[]): void {
    this.router.navigate(url);
  }

  closeMenuList(): void {
    this.tableData.forEach((item) => {
      item.showThreeDotMenu = false;
    });
  }

  toggleFilterMenu(): void {
    this.showFilterMenu = !this.showFilterMenu;
  }

  closeFilterMenu(): void {
    this.showFilterMenu = false;
  }

  onFilterSelect(value: AudienceStatus): void {
    this.selectedFilter = value;
    this.applyFilter();
    this.closeFilterMenu();
  }

  applyFilter(): void {
    let filteredData = [...this.originalTableData];

    switch (this.selectedFilter) {
      case AudienceStatus.Active:
        filteredData = filteredData.filter((item) => item.status === AudienceStatus.Active);
        break;
      case AudienceStatus.Inactive:
        filteredData = filteredData.filter((item) => item.status === AudienceStatus.Inactive);
        break;
    }

    this.tableData = filteredData;
  }

  navigateToStage(item: any): void {
    this.router.navigate([getOfferBuilderRoot(this.currentSection), 'stage', item.segment_id, 'segment', item.audience]);
  }

  navigateToStageSettings(item: any): void {
    if (item.survey) {
      const defaultSurveyFunnel = this.surveyStep ? this.surveyStep.funnel : null;

      if (item.survey.funnel && +item.survey.funnel !== +defaultSurveyFunnel) {
        // segment already has its own segmented survey
        this.openStageSettings(item.survey.funnel);
      } else {
        // no segmented survey yet: generate one from the default survey step, then open it
        this.generateSegmentSurvey(item);
      }
    }
  }

  generateSegmentSurvey(item: any): void {
    this.loader.show();
    this.funnelStepService.setFunnelId(this.surveyStep.funnel);
    const campaignId = this.currentSection === OfferBuilderSection.Resolve
      ? null : +this.campaignService.getCurrentCampaign().id;
    this.funnelStepService
      .generateSurveySegment(this.surveyStep.id, campaignId, +item.audience)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (survey: Funnel) => {
          this.loader.hide();
          this.openStageSettings(survey.id);
        },
        (error) => {
          this.loader.hide();
          this.handleError(error);
        }
      );
  }

  openStageSettings(surveyFunnelId: number | string): void {
    this.router.navigate([
      getOfferBuilderRoot(this.currentSection),
      surveyFunnelId,
      'intent',
      this.offer.offer_intent,
      'settings',
    ]);
  }

  generateBreadcrumbs(): void {
    this.breadcrumbs = [
      {
        label: 'Offer Builder',
        url: [getOfferBuilderRoot(this.currentSection)],
      },
      {
        label: 'Segments',
      },
    ];
  }

  getPopupCustomClass(popupType: AudiencePopup): string {
    return popupType === AudiencePopup.Conditions
      ? 'nsm-dialog-md nsm-centered type-popup'
      : 'nsm-dialog-md nsm-centered';
  }

  get selectedFilterOption() {
    return this.selectedFilter === AudienceStatus.Active
      ? AudienceStatusLabels[AudienceStatus.Active]
      : AudienceStatusLabels[AudienceStatus.Inactive];
  }

  get offerName(): string {
    return this.offerData ? this.offerData.offerName : '--';
  }

  get activeFilterSelected(): boolean {
    return this.selectedFilter === AudienceStatus.Active;
  }
}
